import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, DollarSign, Target, BarChart3, PieChart,
  Calendar, Sparkles, RefreshCw, Plus, Download, Share2, AlertTriangle,
  CheckCircle, Clock, Package, Briefcase, Users, Building, ArrowUpRight,
  ArrowDownRight, Loader2, FileText, Settings, Eye, ChevronRight,
  Printer, Edit, Trash2, Save, LayoutGrid, List, FileBarChart,
  Calculator, Gauge, AlertCircle, CheckCircle2, XCircle
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell } from 'recharts';
import api from '../api';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const REVENUE_STREAMS = [
  { id: 'equipment_home_small', label: 'Home / Small Business Units', category: 'equipment' },
  { id: 'equipment_scientific_specialty', label: 'Scientific / Specialty Units', category: 'equipment' },
  { id: 'trial_testing_services', label: 'Trial & Testing Services', category: 'services' },
  { id: 'consulting_recipe_dev', label: 'Consulting / Recipe Development', category: 'services' },
  { id: 'accessories_consumables', label: 'Accessories & Consumables', category: 'products' },
  { id: 'warranty_sla_services', label: 'Warranty / SLA / Services', category: 'services' },
  { id: 'grants_non_operating', label: 'Grants or Non-Operating Revenue', category: 'other' }
];

const CONFIDENCE_TIERS = [
  { id: 'committed', label: 'Committed (PO in hand)', weight: 1.0, color: 'bg-emerald-100 text-emerald-700' },
  { id: 'high_probability', label: 'High Probability (verbal + timeline)', weight: 0.75, color: 'bg-blue-100 text-blue-700' },
  { id: 'medium_probability', label: 'Medium Probability', weight: 0.50, color: 'bg-amber-100 text-amber-700' },
  { id: 'low_probability', label: 'Low Probability / Pipeline', weight: 0.25, color: 'bg-slate-100 text-slate-700' }
];

const EXPENSE_CATEGORIES = {
  variable: [
    { id: 'commissions', label: 'Commissions' },
    { id: 'referral_fees', label: 'Referral Fees' },
    { id: 'channel_margins', label: 'Channel Margins' },
    { id: 'promo_discounts', label: 'Promo Discounts' },
    { id: 'demo_trial_units', label: 'Demo / Trial Units' }
  ],
  fixed: [
    { id: 'salaries_benefits', label: 'Salaries & Benefits' },
    { id: 'travel', label: 'Travel' },
    { id: 'trade_shows', label: 'Trade Shows' },
    { id: 'marketing_support', label: 'Marketing Support' },
    { id: 'crm_tools', label: 'CRM and Sales Tools' }
  ]
};

const SalesForecastingPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [forecasts, setForecasts] = useState([]);
  const [activeForecast, setActiveForecast] = useState(null);
  const [letterhead, setLetterhead] = useState(null);
  const [products, setProducts] = useState([]);
  
  // Section data
  const [unitForecasts, setUnitForecasts] = useState([]);
  const [confidenceSummary, setConfidenceSummary] = useState(null);
  const [salesTimingEntries, setSalesTimingEntries] = useState([]);
  const [variableCosts, setVariableCosts] = useState([]);
  const [fixedCosts, setFixedCosts] = useState([]);
  const [capacityConstraints, setCapacityConstraints] = useState([]);
  const [variances, setVariances] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [assumptions, setAssumptions] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  
  // UI State
  const [loadingAI, setLoadingAI] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showLetterheadDialog, setShowLetterheadDialog] = useState(false);
  const [showAddProductDialog, setShowAddProductDialog] = useState(false);
  const [editingUnitForecast, setEditingUnitForecast] = useState(null);
  
  // Forms
  const [newForecast, setNewForecast] = useState({
    name: '',
    fiscal_period_type: 'month',
    fiscal_period_value: new Date().toISOString().slice(0, 7),
    version_type: 'forecast_v1',
    owner_type: 'sales',
    notes: ''
  });
  
  const [letterheadForm, setLetterheadForm] = useState({
    company_name: '',
    logo_url: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    primary_color: '#1e3a5f',
    secondary_color: '#3b82f6'
  });

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    try {
      const res = await api.get('/sales-forecasting/dashboard');
      setDashboard(res.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  }, []);

  // Load forecasts
  const loadForecasts = useCallback(async () => {
    try {
      const res = await api.get('/sales-forecasting/forecasts');
      setForecasts(res.data.forecasts || []);
      const active = res.data.forecasts?.find(f => f.status === 'active');
      if (active) {
        setActiveForecast(active);
        loadForecastData(active.id);
      }
    } catch (error) {
      console.error('Error loading forecasts:', error);
    }
  }, []);

  // Load letterhead
  const loadLetterhead = useCallback(async () => {
    try {
      const res = await api.get('/sales-forecasting/letterhead');
      setLetterhead(res.data);
      setLetterheadForm(res.data);
    } catch (error) {
      console.error('Error loading letterhead:', error);
    }
  }, []);

  // Load products for forecasting
  const loadProducts = useCallback(async () => {
    try {
      const res = await api.get('/products');
      setProducts(res.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  }, []);

  // Load forecast-specific data
  const loadForecastData = async (forecastId) => {
    try {
      const res = await api.get(`/sales-forecasting/forecasts/${forecastId}`);
      const forecast = res.data;
      
      setUnitForecasts(forecast.unit_forecasts || []);
      setConfidenceSummary(forecast.confidence_summary || null);
      setSalesTimingEntries(forecast.sales_timing_entries || []);
      setVariableCosts(forecast.variable_costs || []);
      setFixedCosts(forecast.fixed_costs || []);
      setCapacityConstraints(forecast.capacity_constraints || []);
      setVariances(forecast.variances || []);
      setMetrics(forecast.metrics || null);
      setAssumptions(forecast.assumptions || []);
      setAiAnalysis(forecast.ai_analysis || null);
    } catch (error) {
      console.error('Error loading forecast data:', error);
    }
  };

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        loadDashboard(),
        loadForecasts(),
        loadLetterhead(),
        loadProducts()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [loadDashboard, loadForecasts, loadLetterhead, loadProducts]);

  // Create forecast
  const createForecast = async () => {
    try {
      const res = await api.post('/sales-forecasting/forecasts', newForecast);
      toast.success('Forecast created');
      setShowCreateDialog(false);
      setActiveForecast(res.data);
      loadForecasts();
    } catch (error) {
      toast.error('Failed to create forecast');
    }
  };

  // Update letterhead
  const updateLetterhead = async () => {
    try {
      await api.put('/sales-forecasting/letterhead', letterheadForm);
      toast.success('Letterhead updated');
      setShowLetterheadDialog(false);
      loadLetterhead();
    } catch (error) {
      toast.error('Failed to update letterhead');
    }
  };

  // Save unit forecasts
  const saveUnitForecasts = async () => {
    if (!activeForecast) return;
    try {
      const res = await api.post(`/sales-forecasting/forecasts/${activeForecast.id}/unit-forecasts`, unitForecasts);
      setConfidenceSummary(res.data.confidence_summary);
      toast.success('Unit forecasts saved');
    } catch (error) {
      toast.error('Failed to save unit forecasts');
    }
  };

  // Add unit forecast entry
  const addUnitForecastEntry = (product) => {
    const newEntry = {
      product_id: product.id,
      product_name: product.name,
      revenue_stream: 'equipment_home_small',
      beginning_pipeline_units: 0,
      new_opportunities_units: 0,
      units_closed_won: 0,
      units_closed_lost: 0,
      units_deferred: 0,
      ending_pipeline_units: 0,
      expected_units_sold: 0,
      average_selling_price: product.price || 0,
      gross_revenue: 0,
      discounts_incentives: 0,
      net_revenue: 0,
      committed_units: 0,
      high_probability_units: 0,
      medium_probability_units: 0,
      low_probability_units: 0,
      weighted_revenue: 0,
      notes: ''
    };
    setUnitForecasts([...unitForecasts, newEntry]);
    setShowAddProductDialog(false);
    toast.success(`Added ${product.name} to forecast`);
  };

  // Update unit forecast entry
  const updateUnitForecastEntry = (index, field, value) => {
    const updated = [...unitForecasts];
    updated[index][field] = value;
    
    // Auto-calculate derived fields
    const entry = updated[index];
    entry.gross_revenue = entry.expected_units_sold * entry.average_selling_price;
    entry.net_revenue = entry.gross_revenue - entry.discounts_incentives;
    entry.ending_pipeline_units = entry.beginning_pipeline_units + entry.new_opportunities_units 
      - entry.units_closed_won - entry.units_closed_lost - entry.units_deferred;
    entry.weighted_revenue = 
      (entry.committed_units * entry.average_selling_price * 1.0) +
      (entry.high_probability_units * entry.average_selling_price * 0.75) +
      (entry.medium_probability_units * entry.average_selling_price * 0.50) +
      (entry.low_probability_units * entry.average_selling_price * 0.25);
    
    setUnitForecasts(updated);
  };

  // Run AI analysis
  const runAIAnalysis = async (analysisType) => {
    if (!activeForecast) {
      toast.error('Please select a forecast first');
      return;
    }
    
    try {
      setLoadingAI(true);
      const res = await api.post('/sales-forecasting/ai/analyze', {
        forecast_id: activeForecast.id,
        analysis_type: analysisType,
        include_recommendations: true
      });
      
      if (res.data.success) {
        setAiAnalysis(prev => ({
          ...prev,
          [analysisType]: res.data.result
        }));
        toast.success(`${analysisType.replace('_', ' ')} analysis complete`);
      } else {
        toast.error(res.data.error || 'Analysis failed');
      }
    } catch (error) {
      toast.error('Failed to run AI analysis');
    } finally {
      setLoadingAI(false);
    }
  };

  // Calculate metrics
  const calculateMetrics = async () => {
    if (!activeForecast) return;
    try {
      const res = await api.get(`/sales-forecasting/forecasts/${activeForecast.id}/metrics`);
      setMetrics(res.data.metrics);
      toast.success('Metrics calculated');
    } catch (error) {
      toast.error('Failed to calculate metrics');
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;

  const getVarianceColor = (variance) => {
    if (variance > 0) return 'text-emerald-600';
    if (variance < 0) return 'text-red-600';
    return 'text-slate-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="forecasting-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const exec = dashboard?.executive_summary || {};

  return (
    <div className="p-6 space-y-6" data-testid="sales-forecasting-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sales Forecasting & Budgeting</h1>
          <p className="text-slate-500 mt-1">Unit-based forecasting with confidence weighting & AI insights</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowLetterheadDialog(true)} data-testid="letterhead-btn">
            <Printer className="h-4 w-4 mr-2" />
            Letterhead
          </Button>
          <Button variant="outline" onClick={() => runAIAnalysis('ceo_review')} disabled={loadingAI || !activeForecast}>
            {loadingAI ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            CEO Review
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="new-forecast-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Forecast
          </Button>
        </div>
      </div>

      {/* Executive Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">MTD Actual</p>
                <p className="text-2xl font-bold">{formatCurrency(exec.mtd_actual)}</p>
                <p className="text-xs text-slate-400">{exec.mtd_deals || 0} deals closed</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">YTD Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(exec.ytd_actual)}</p>
                <p className="text-xs text-slate-400">{exec.ytd_deals || 0} deals closed</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pipeline Value</p>
                <p className="text-2xl font-bold">{formatCurrency(exec.pipeline_value)}</p>
                <p className="text-xs text-slate-400">{exec.pipeline_deals || 0} open deals</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Target className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Forecast</p>
                <p className="text-lg font-bold truncate max-w-[150px]">{activeForecast?.name || 'None'}</p>
                <p className="text-xs text-slate-400">{activeForecast?.fiscal_period_value || '-'}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <FileBarChart className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Forecast Selector */}
      {forecasts.length > 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Label>Select Forecast:</Label>
              <Select 
                value={activeForecast?.id || ''} 
                onValueChange={(id) => {
                  const fc = forecasts.find(f => f.id === id);
                  setActiveForecast(fc);
                  if (fc) loadForecastData(fc.id);
                }}
              >
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder="Choose a forecast" />
                </SelectTrigger>
                <SelectContent>
                  {forecasts.map(fc => (
                    <SelectItem key={fc.id} value={fc.id}>
                      {fc.name} ({fc.fiscal_period_value}) - {fc.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {activeForecast && activeForecast.status !== 'active' && (
                <Button variant="outline" size="sm" onClick={async () => {
                  await api.put(`/sales-forecasting/forecasts/${activeForecast.id}`, { status: 'active' });
                  toast.success('Forecast activated');
                  loadForecasts();
                }}>
                  Set as Active
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs - 10 Sections */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-4xl">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="forecast">Unit Forecast</TabsTrigger>
          <TabsTrigger value="confidence">Confidence</TabsTrigger>
          <TabsTrigger value="timing">Timing & Cash</TabsTrigger>
          <TabsTrigger value="budget">Budget</TabsTrigger>
        </TabsList>
        <TabsList className="grid grid-cols-5 w-full max-w-4xl mt-2">
          <TabsTrigger value="capacity">Capacity</TabsTrigger>
          <TabsTrigger value="variance">Variance</TabsTrigger>
          <TabsTrigger value="metrics">Metrics</TabsTrigger>
          <TabsTrigger value="assumptions">Assumptions</TabsTrigger>
          <TabsTrigger value="ai">AI Insights</TabsTrigger>
        </TabsList>

        {/* Tab 1: Dashboard */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Confidence Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Confidence Weighting Summary</CardTitle>
                <CardDescription>Conservative vs Planning Forecast</CardDescription>
              </CardHeader>
              <CardContent>
                {confidenceSummary ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-emerald-50 rounded-lg">
                        <p className="text-sm text-emerald-600 font-medium">Conservative (Committed + High)</p>
                        <p className="text-2xl font-bold text-emerald-700">
                          {formatCurrency(confidenceSummary.conservative_forecast)}
                        </p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-600 font-medium">Planning (Weighted Total)</p>
                        <p className="text-2xl font-bold text-blue-700">
                          {formatCurrency(confidenceSummary.planning_forecast)}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {CONFIDENCE_TIERS.map(tier => (
                        <div key={tier.id} className="flex justify-between items-center">
                          <Badge className={tier.color}>{tier.label}</Badge>
                          <span className="font-medium">
                            {formatCurrency(confidenceSummary[`${tier.id}_total`])}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">No forecast data yet</p>
                )}
              </CardContent>
            </Card>

            {/* Key Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Key Sales Metrics</CardTitle>
                <CardDescription>Auto-calculated from forecast data</CardDescription>
              </CardHeader>
              <CardContent>
                {metrics ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 border rounded-lg">
                      <p className="text-sm text-slate-500">Win Rate</p>
                      <p className="text-xl font-bold">{formatPercent(metrics.win_rate)}</p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="text-sm text-slate-500">Avg Deal Size</p>
                      <p className="text-xl font-bold">{formatCurrency(metrics.average_deal_size)}</p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="text-sm text-slate-500">Sales Cycle</p>
                      <p className="text-xl font-bold">{metrics.sales_cycle_length_days} days</p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="text-sm text-slate-500">Forecast Accuracy (3mo)</p>
                      <p className="text-xl font-bold">{formatPercent(metrics.forecast_accuracy_3mo)}</p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="text-sm text-slate-500">Revenue per Rep</p>
                      <p className="text-xl font-bold">{formatCurrency(metrics.revenue_per_sales_rep)}</p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="text-sm text-slate-500">Total Units Forecast</p>
                      <p className="text-xl font-bold">{metrics.total_units_forecast}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-slate-500 mb-4">No metrics calculated yet</p>
                    <Button onClick={calculateMetrics} disabled={!activeForecast}>
                      <Calculator className="h-4 w-4 mr-2" />
                      Calculate Metrics
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Forecasts */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Forecasts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {forecasts.slice(0, 5).map(fc => (
                  <div 
                    key={fc.id} 
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                      activeForecast?.id === fc.id ? 'bg-blue-50 border-blue-200 border' : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                    onClick={() => {
                      setActiveForecast(fc);
                      loadForecastData(fc.id);
                    }}
                  >
                    <div>
                      <p className="font-medium">{fc.name}</p>
                      <p className="text-sm text-slate-500">{fc.fiscal_period_value} • {fc.owner_type}</p>
                    </div>
                    <Badge variant={fc.status === 'active' ? 'default' : 'secondary'}>{fc.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 2: Unit-Based Sales Forecast */}
        <TabsContent value="forecast" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Unit-Based Sales Forecast</h3>
              <p className="text-sm text-slate-500">Forecast by product with pipeline tracking</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAddProductDialog(true)} disabled={!activeForecast}>
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
              <Button onClick={saveUnitForecasts} disabled={!activeForecast || unitForecasts.length === 0}>
                <Save className="h-4 w-4 mr-2" />
                Save Forecast
              </Button>
            </div>
          </div>

          {unitForecasts.length > 0 ? (
            <Card>
              <CardContent className="pt-6">
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="sticky left-0 bg-white">Product</TableHead>
                        <TableHead>Revenue Stream</TableHead>
                        <TableHead className="text-right">Begin Pipeline</TableHead>
                        <TableHead className="text-right">New Opps</TableHead>
                        <TableHead className="text-right">Won</TableHead>
                        <TableHead className="text-right">Lost</TableHead>
                        <TableHead className="text-right">End Pipeline</TableHead>
                        <TableHead className="text-right">Expected Units</TableHead>
                        <TableHead className="text-right">ASP</TableHead>
                        <TableHead className="text-right">Gross Rev</TableHead>
                        <TableHead className="text-right">Discounts</TableHead>
                        <TableHead className="text-right">Net Revenue</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {unitForecasts.map((entry, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium sticky left-0 bg-white">{entry.product_name}</TableCell>
                          <TableCell>
                            <Select 
                              value={entry.revenue_stream} 
                              onValueChange={(v) => updateUnitForecastEntry(idx, 'revenue_stream', v)}
                            >
                              <SelectTrigger className="w-[180px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {REVENUE_STREAMS.map(rs => (
                                  <SelectItem key={rs.id} value={rs.id}>{rs.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={entry.beginning_pipeline_units} 
                              onChange={(e) => updateUnitForecastEntry(idx, 'beginning_pipeline_units', parseInt(e.target.value) || 0)}
                              className="w-20 text-right"
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={entry.new_opportunities_units} 
                              onChange={(e) => updateUnitForecastEntry(idx, 'new_opportunities_units', parseInt(e.target.value) || 0)}
                              className="w-20 text-right"
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={entry.units_closed_won} 
                              onChange={(e) => updateUnitForecastEntry(idx, 'units_closed_won', parseInt(e.target.value) || 0)}
                              className="w-20 text-right"
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={entry.units_closed_lost} 
                              onChange={(e) => updateUnitForecastEntry(idx, 'units_closed_lost', parseInt(e.target.value) || 0)}
                              className="w-20 text-right"
                            />
                          </TableCell>
                          <TableCell className="text-right font-medium">{entry.ending_pipeline_units}</TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={entry.expected_units_sold} 
                              onChange={(e) => updateUnitForecastEntry(idx, 'expected_units_sold', parseInt(e.target.value) || 0)}
                              className="w-20 text-right"
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={entry.average_selling_price} 
                              onChange={(e) => updateUnitForecastEntry(idx, 'average_selling_price', parseFloat(e.target.value) || 0)}
                              className="w-24 text-right"
                            />
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.gross_revenue)}</TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={entry.discounts_incentives} 
                              onChange={(e) => updateUnitForecastEntry(idx, 'discounts_incentives', parseFloat(e.target.value) || 0)}
                              className="w-24 text-right"
                            />
                          </TableCell>
                          <TableCell className="text-right font-bold">{formatCurrency(entry.net_revenue)}</TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => {
                                setUnitForecasts(unitForecasts.filter((_, i) => i !== idx));
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="font-medium text-slate-900 mb-2">No Products in Forecast</h3>
                <p className="text-slate-500 mb-4">Add products to start building your unit-based forecast</p>
                <Button onClick={() => setShowAddProductDialog(true)} disabled={!activeForecast}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab 3: Confidence Weighting */}
        <TabsContent value="confidence" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Confidence Weighting</h3>
              <p className="text-sm text-slate-500">Assign units by confidence tier for weighted forecasting</p>
            </div>
          </div>

          {unitForecasts.length > 0 ? (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Committed (100%)</TableHead>
                      <TableHead className="text-right">High Prob (75%)</TableHead>
                      <TableHead className="text-right">Medium (50%)</TableHead>
                      <TableHead className="text-right">Low / Pipeline (25%)</TableHead>
                      <TableHead className="text-right">Weighted Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {unitForecasts.map((entry, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{entry.product_name}</TableCell>
                        <TableCell>
                          <Input 
                            type="number" 
                            value={entry.committed_units} 
                            onChange={(e) => updateUnitForecastEntry(idx, 'committed_units', parseInt(e.target.value) || 0)}
                            className="w-24 text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number" 
                            value={entry.high_probability_units} 
                            onChange={(e) => updateUnitForecastEntry(idx, 'high_probability_units', parseInt(e.target.value) || 0)}
                            className="w-24 text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number" 
                            value={entry.medium_probability_units} 
                            onChange={(e) => updateUnitForecastEntry(idx, 'medium_probability_units', parseInt(e.target.value) || 0)}
                            className="w-24 text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number" 
                            value={entry.low_probability_units} 
                            onChange={(e) => updateUnitForecastEntry(idx, 'low_probability_units', parseInt(e.target.value) || 0)}
                            className="w-24 text-right"
                          />
                        </TableCell>
                        <TableCell className="text-right font-bold text-blue-600">
                          {formatCurrency(entry.weighted_revenue)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Separator className="my-6" />

                <div className="grid grid-cols-2 gap-6">
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-sm font-medium text-emerald-700">Conservative Forecast</p>
                    <p className="text-xs text-emerald-600 mb-2">(Committed + High Probability)</p>
                    <p className="text-3xl font-bold text-emerald-700">
                      {formatCurrency(
                        unitForecasts.reduce((sum, e) => sum + 
                          (e.committed_units * e.average_selling_price) + 
                          (e.high_probability_units * e.average_selling_price * 0.75), 0)
                      )}
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-700">Planning Forecast</p>
                    <p className="text-xs text-blue-600 mb-2">(Weighted Total)</p>
                    <p className="text-3xl font-bold text-blue-700">
                      {formatCurrency(unitForecasts.reduce((sum, e) => sum + e.weighted_revenue, 0))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Gauge className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="font-medium text-slate-900 mb-2">No Data for Confidence Weighting</h3>
                <p className="text-slate-500">Add products to your unit forecast first</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab 4: Sales Timing & Cash Collection */}
        <TabsContent value="timing" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Sales Timing & Cash Collection</h3>
              <p className="text-sm text-slate-500">Track order dates, invoicing, and payment timing</p>
            </div>
          </div>

          <Card>
            <CardContent className="py-12 text-center">
              <Clock className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="font-medium text-slate-900 mb-2">Sales Timing Coming Soon</h3>
              <p className="text-slate-500 mb-4">Configure order → delivery → invoice → payment timing</p>
              <div className="text-left max-w-md mx-auto space-y-2 text-sm text-slate-600">
                <p>• Order date tracking</p>
                <p>• Delivery / Install date</p>
                <p>• Invoice date</p>
                <p>• Payment terms (Net 0/30/60)</p>
                <p>• Monthly revenue recognition</p>
                <p>• Monthly cash inflow</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 5: Sales Budget (Expense Side) */}
        <TabsContent value="budget" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Sales Budget (Expense Side)</h3>
              <p className="text-sm text-slate-500">Track variable and fixed sales costs</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Variable Sales Costs</CardTitle>
                <CardDescription>Costs that vary with sales volume</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {EXPENSE_CATEGORIES.variable.map(cat => (
                    <div key={cat.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm font-medium">{cat.label}</span>
                      <Input type="number" placeholder="$0" className="w-32 text-right" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Fixed Sales Costs</CardTitle>
                <CardDescription>Monthly fixed expenses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {EXPENSE_CATEGORIES.fixed.map(cat => (
                    <div key={cat.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm font-medium">{cat.label}</span>
                      <Input type="number" placeholder="$0" className="w-32 text-right" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab 6: Capacity & Constraints */}
        <TabsContent value="capacity" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Capacity & Constraint Check</h3>
              <p className="text-sm text-slate-500">Ensure forecast doesn't exceed production capacity</p>
            </div>
          </div>

          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="font-medium text-slate-900 mb-2">Capacity Tracking Coming Soon</h3>
              <p className="text-slate-500 mb-4">Track production constraints by product</p>
              <div className="text-left max-w-md mx-auto space-y-2 text-sm text-slate-600">
                <p>• Production capacity (units/month)</p>
                <p>• Lead times</p>
                <p>• Engineering bottlenecks</p>
                <p>• Inventory availability</p>
                <p>• Constraint violation alerts</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 7: Variance Tracking */}
        <TabsContent value="variance" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Variance Tracking</h3>
              <p className="text-sm text-slate-500">Forecast vs Actual comparison</p>
            </div>
          </div>

          <Card>
            <CardContent className="pt-6">
              {variances.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Forecast Units</TableHead>
                      <TableHead className="text-right">Actual Units</TableHead>
                      <TableHead className="text-right">Variance</TableHead>
                      <TableHead className="text-right">Forecast $</TableHead>
                      <TableHead className="text-right">Actual $</TableHead>
                      <TableHead className="text-right">Variance %</TableHead>
                      <TableHead>Root Cause</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {variances.map((v, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{v.period}</TableCell>
                        <TableCell>{v.product_id || 'All'}</TableCell>
                        <TableCell className="text-right">{v.forecast_units}</TableCell>
                        <TableCell className="text-right">{v.actual_units}</TableCell>
                        <TableCell className={`text-right ${getVarianceColor(v.variance_units)}`}>
                          {v.variance_units > 0 ? '+' : ''}{v.variance_units}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(v.forecast_revenue)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(v.actual_revenue)}</TableCell>
                        <TableCell className={`text-right ${getVarianceColor(-Math.abs(v.variance_percentage))}`}>
                          {v.variance_percentage > 0 ? '+' : ''}{formatPercent(v.variance_percentage)}
                        </TableCell>
                        <TableCell>{v.root_cause || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-12 text-center">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <h3 className="font-medium text-slate-900 mb-2">No Variance Data</h3>
                  <p className="text-slate-500">Record actuals to track forecast accuracy</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 8: Key Sales Metrics */}
        <TabsContent value="metrics" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Key Sales Metrics</h3>
              <p className="text-sm text-slate-500">Auto-calculated performance indicators</p>
            </div>
            <Button onClick={calculateMetrics} disabled={!activeForecast}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Recalculate
            </Button>
          </div>

          {metrics ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Win Rate</p>
                      <p className="text-3xl font-bold">{formatPercent(metrics.win_rate)}</p>
                    </div>
                    <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Average Deal Size</p>
                      <p className="text-3xl font-bold">{formatCurrency(metrics.average_deal_size)}</p>
                    </div>
                    <DollarSign className="h-8 w-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Sales Cycle Length</p>
                      <p className="text-3xl font-bold">{metrics.sales_cycle_length_days} days</p>
                    </div>
                    <Clock className="h-8 w-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Revenue per Rep</p>
                      <p className="text-3xl font-bold">{formatCurrency(metrics.revenue_per_sales_rep)}</p>
                    </div>
                    <Users className="h-8 w-8 text-amber-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Forecast Accuracy (3mo)</p>
                      <p className="text-3xl font-bold">{formatPercent(metrics.forecast_accuracy_3mo)}</p>
                    </div>
                    <Target className="h-8 w-8 text-pink-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total Units Forecast</p>
                      <p className="text-3xl font-bold">{metrics.total_units_forecast}</p>
                    </div>
                    <Package className="h-8 w-8 text-cyan-500" />
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Calculator className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="font-medium text-slate-900 mb-2">No Metrics Calculated</h3>
                <p className="text-slate-500 mb-4">Add forecast data and click Recalculate</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab 9: Assumptions & Notes */}
        <TabsContent value="assumptions" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Assumptions & Notes</h3>
              <p className="text-sm text-slate-500">Document pricing, discount policies, and known risks</p>
            </div>
            <Button variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Add Assumption
            </Button>
          </div>

          <Card>
            <CardContent className="pt-6">
              {assumptions.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Assumption</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Impact</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assumptions.map((a, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Badge variant="outline">{a.category}</Badge>
                        </TableCell>
                        <TableCell>{a.assumption}</TableCell>
                        <TableCell>{a.value || '-'}</TableCell>
                        <TableCell>{a.impact || '-'}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-12 text-center">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <h3 className="font-medium text-slate-900 mb-2">No Assumptions Documented</h3>
                  <p className="text-slate-500 max-w-md mx-auto">
                    Document pricing assumptions, discount policies, expected conversion rates, 
                    known risks, and one-time vs recurring revenue
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 10: AI Insights */}
        <TabsContent value="ai" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">AI-Powered Insights</h3>
              <p className="text-sm text-slate-500">CEO review, SWOT analysis, and recommendations</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col"
              onClick={() => runAIAnalysis('ceo_review')}
              disabled={loadingAI || !activeForecast}
            >
              <Briefcase className="h-6 w-6 mb-2" />
              CEO Review
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col"
              onClick={() => runAIAnalysis('swot')}
              disabled={loadingAI || !activeForecast}
            >
              <LayoutGrid className="h-6 w-6 mb-2" />
              SWOT Analysis
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col"
              onClick={() => runAIAnalysis('at_risk_deals')}
              disabled={loadingAI || !activeForecast}
            >
              <AlertTriangle className="h-6 w-6 mb-2" />
              At-Risk Deals
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col"
              onClick={() => runAIAnalysis('completeness')}
              disabled={loadingAI || !activeForecast}
            >
              <CheckCircle className="h-6 w-6 mb-2" />
              Completeness Score
            </Button>
          </div>

          {loadingAI && (
            <Card>
              <CardContent className="py-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
                <p>Generating AI analysis...</p>
              </CardContent>
            </Card>
          )}

          {aiAnalysis && (
            <div className="space-y-4">
              {/* CEO Review */}
              {aiAnalysis.ceo_review && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Briefcase className="h-5 w-5 text-blue-500" />
                      CEO Review
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 mb-4">{aiAnalysis.ceo_review.executive_summary}</p>
                    
                    {aiAnalysis.ceo_review.key_highlights && (
                      <div className="mb-4">
                        <p className="font-medium mb-2">Key Highlights:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-slate-600">
                          {aiAnalysis.ceo_review.key_highlights.map((h, i) => (
                            <li key={i}>{h}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {aiAnalysis.ceo_review.concerns && aiAnalysis.ceo_review.concerns.length > 0 && (
                      <div className="mb-4">
                        <p className="font-medium mb-2 text-amber-600">Concerns:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-amber-700">
                          {aiAnalysis.ceo_review.concerns.map((c, i) => (
                            <li key={i}>{c}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <Badge variant={
                      aiAnalysis.ceo_review.confidence_level === 'high' ? 'default' :
                      aiAnalysis.ceo_review.confidence_level === 'medium' ? 'secondary' : 'destructive'
                    }>
                      Confidence: {aiAnalysis.ceo_review.confidence_level}
                    </Badge>
                  </CardContent>
                </Card>
              )}

              {/* SWOT */}
              {aiAnalysis.swot && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <LayoutGrid className="h-5 w-5 text-purple-500" />
                      SWOT Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-emerald-50 rounded-lg">
                        <p className="font-medium text-emerald-700 mb-2">Strengths</p>
                        <ul className="list-disc list-inside text-sm text-emerald-600 space-y-1">
                          {(aiAnalysis.swot.strengths || []).map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg">
                        <p className="font-medium text-red-700 mb-2">Weaknesses</p>
                        <ul className="list-disc list-inside text-sm text-red-600 space-y-1">
                          {(aiAnalysis.swot.weaknesses || []).map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="font-medium text-blue-700 mb-2">Opportunities</p>
                        <ul className="list-disc list-inside text-sm text-blue-600 space-y-1">
                          {(aiAnalysis.swot.opportunities || []).map((o, i) => <li key={i}>{o}</li>)}
                        </ul>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg">
                        <p className="font-medium text-amber-700 mb-2">Threats</p>
                        <ul className="list-disc list-inside text-sm text-amber-600 space-y-1">
                          {(aiAnalysis.swot.threats || []).map((t, i) => <li key={i}>{t}</li>)}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Completeness Score */}
              {aiAnalysis.completeness && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                      Completeness Score
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="text-4xl font-bold">
                        {aiAnalysis.completeness.completeness_score}%
                      </div>
                      <Progress value={aiAnalysis.completeness.completeness_score} className="flex-1" />
                    </div>
                    
                    {aiAnalysis.completeness.sections_missing && aiAnalysis.completeness.sections_missing.length > 0 && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="font-medium text-amber-700 mb-2">Missing Sections:</p>
                        <ul className="list-disc list-inside text-sm text-amber-600">
                          {aiAnalysis.completeness.sections_missing.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {!aiAnalysis && !loadingAI && (
            <Card>
              <CardContent className="py-12 text-center">
                <Sparkles className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="font-medium text-slate-900 mb-2">No AI Analysis Yet</h3>
                <p className="text-slate-500">Select an analysis type above to generate AI insights</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Forecast Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Forecast</DialogTitle>
            <DialogDescription>Set up a new sales forecast with header controls</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Forecast Name</Label>
              <Input
                value={newForecast.name}
                onChange={(e) => setNewForecast({ ...newForecast, name: e.target.value })}
                placeholder="e.g., Q1 2026 Sales Forecast"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Type</Label>
                <Select 
                  value={newForecast.fiscal_period_type} 
                  onValueChange={(v) => setNewForecast({ ...newForecast, fiscal_period_type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Monthly</SelectItem>
                    <SelectItem value="quarter">Quarterly</SelectItem>
                    <SelectItem value="fy">Fiscal Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Period</Label>
                <Input
                  value={newForecast.fiscal_period_value}
                  onChange={(e) => setNewForecast({ ...newForecast, fiscal_period_value: e.target.value })}
                  placeholder="2026-01 or Q1 or FY2026"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Version</Label>
                <Select 
                  value={newForecast.version_type} 
                  onValueChange={(v) => setNewForecast({ ...newForecast, version_type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="budget">Budget</SelectItem>
                    <SelectItem value="forecast_v1">Forecast v1.0</SelectItem>
                    <SelectItem value="forecast_v2">Forecast v2.0</SelectItem>
                    <SelectItem value="actual">Actual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Owner</Label>
                <Select 
                  value={newForecast.owner_type} 
                  onValueChange={(v) => setNewForecast({ ...newForecast, owner_type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newForecast.notes}
                onChange={(e) => setNewForecast({ ...newForecast, notes: e.target.value })}
                placeholder="Add any notes about this forecast..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createForecast} disabled={!newForecast.name}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Letterhead Settings Dialog */}
      <Dialog open={showLetterheadDialog} onOpenChange={setShowLetterheadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Report Letterhead Settings</DialogTitle>
            <DialogDescription>Customize the letterhead for PDF reports</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 pr-4">
              <div>
                <Label>Company Name</Label>
                <Input
                  value={letterheadForm.company_name}
                  onChange={(e) => setLetterheadForm({ ...letterheadForm, company_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Logo URL</Label>
                <Input
                  value={letterheadForm.logo_url}
                  onChange={(e) => setLetterheadForm({ ...letterheadForm, logo_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <Separator />
              <div>
                <Label>Address Line 1</Label>
                <Input
                  value={letterheadForm.address_line1}
                  onChange={(e) => setLetterheadForm({ ...letterheadForm, address_line1: e.target.value })}
                />
              </div>
              <div>
                <Label>Address Line 2</Label>
                <Input
                  value={letterheadForm.address_line2}
                  onChange={(e) => setLetterheadForm({ ...letterheadForm, address_line2: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>City</Label>
                  <Input
                    value={letterheadForm.city}
                    onChange={(e) => setLetterheadForm({ ...letterheadForm, city: e.target.value })}
                  />
                </div>
                <div>
                  <Label>State/Province</Label>
                  <Input
                    value={letterheadForm.state_province}
                    onChange={(e) => setLetterheadForm({ ...letterheadForm, state_province: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Postal Code</Label>
                  <Input
                    value={letterheadForm.postal_code}
                    onChange={(e) => setLetterheadForm({ ...letterheadForm, postal_code: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Country</Label>
                  <Input
                    value={letterheadForm.country}
                    onChange={(e) => setLetterheadForm({ ...letterheadForm, country: e.target.value })}
                  />
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={letterheadForm.phone}
                    onChange={(e) => setLetterheadForm({ ...letterheadForm, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={letterheadForm.email}
                    onChange={(e) => setLetterheadForm({ ...letterheadForm, email: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={letterheadForm.website}
                  onChange={(e) => setLetterheadForm({ ...letterheadForm, website: e.target.value })}
                />
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Primary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={letterheadForm.primary_color}
                      onChange={(e) => setLetterheadForm({ ...letterheadForm, primary_color: e.target.value })}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={letterheadForm.primary_color}
                      onChange={(e) => setLetterheadForm({ ...letterheadForm, primary_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Secondary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={letterheadForm.secondary_color}
                      onChange={(e) => setLetterheadForm({ ...letterheadForm, secondary_color: e.target.value })}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={letterheadForm.secondary_color}
                      onChange={(e) => setLetterheadForm({ ...letterheadForm, secondary_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLetterheadDialog(false)}>Cancel</Button>
            <Button onClick={updateLetterhead}>Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog open={showAddProductDialog} onOpenChange={setShowAddProductDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Product to Forecast</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-2">
              {products.filter(p => !unitForecasts.some(uf => uf.product_id === p.id)).map(product => (
                <div 
                  key={product.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
                  onClick={() => addUnitForecastEntry(product)}
                >
                  <div>
                    <p className="font-medium">{product.name}</p>
                    <p className="text-sm text-slate-500">{product.sku} • {formatCurrency(product.price)}</p>
                  </div>
                  <Plus className="h-5 w-5 text-slate-400" />
                </div>
              ))}
              {products.filter(p => !unitForecasts.some(uf => uf.product_id === p.id)).length === 0 && (
                <p className="text-center text-slate-500 py-8">All products already added</p>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesForecastingPage;
